import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { css } from '@emotion/react';
import uuid from 'react-uuid';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import t from 'react-translate';

import { useAppDispatch } from 'redux/store';
import { wrapThunkAction } from 'redux/utils';
import {
  getAiRolePlaySubmission,
  getRolePlayGallerySubmissionIds,
  getRolePlayGallerySubmissions,
} from 'redux/selectors/timeline';
import {
  getAiRolePlayConversation,
  getAiRolePlaySubmission as fetchSubmission,
  getAiRolePlaySubmissionsList,
} from 'redux/actions/ai-role-play';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import LoadingWrapper from 'shared/components/loading-wrapper';
import { screenSmMax } from 'styles/global_defaults/media-queries';
import { gray2, gray5, white } from 'styles/global_defaults/colors';
import { largeSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import useAiRolePlay from 'lecture_pages/components/ai-role-play/hooks/use-ai-role-play';

import { AngularContext } from 'react-app';

import AiConversationContext, {
  AIRolePlayStatus,
  AudioRecordingStatus,
  Message,
  MessageStatus,
  SessionData,
} from 'lecture_pages/components/ai-role-play/ai-conversation-modal/context';
import { RootState } from 'redux/schemas';

import ScenarioSection from 'lecture_pages/components/ai-role-play/ai-conversation-modal/scenario-section';
import CompletionSection from 'lecture_pages/components/ai-role-play/ai-conversation-modal/completion-section';
import ScrollHandlerButton from 'lecture_pages/components/ai-role-play/ai-conversation-modal/scroll-handler-button';
import AiFeedback from 'lecture_pages/components/ai-role-play/ai-conversation-modal/ai-feedback';
import NvIcon from 'shared/components/nv-icon';
import { config } from '@config/config.json';
import { RolePlayInteractionMode } from 'redux/schemas/models/ai-role-play';
import ChatTranscriptView from 'lecture_pages/components/ai-role-play/ai-conversation-modal/voice-result/chat-transcript-view';
import VoiceCardsContainer from 'lecture_pages/components/ai-role-play/ai-conversation-modal/voice-mode/voice-cards-container';
import { ClosingReason } from 'lecture_pages/components/ai-role-play/ai-conversation-modal/stream-ai-response';
import VoiceTerminatedStatus from 'lecture_pages/components/ai-role-play/ai-conversation-modal/voice-result/voice-terminated-status';
import prodPathReplace from 'shared/prod-path-rewrite';
import useRolePlayPermissions from '../hooks/use-role-play-permissions';
import SubmissionFeedbackHeader from './submission-feedback-header';
import ConversationReplay from './conversation-replay';
import RolePlaySubmissionModalStyles from './role-play-submission-modal-styles';

const PAGE_SIZE = 10;

const galleryArrowBaseStyles = css`
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1060;
  background: transparent;
  border: none;
  padding: ${standardSpacing}px;
  cursor: pointer;
  color: ${white};
  pointer-events: auto;
`;

const leftArrowStyles = css`
  ${galleryArrowBaseStyles};
  left: 0;
`;

const rightArrowStyles = css`
  ${galleryArrowBaseStyles};
  right: 0;
`;

const galleryArrowDisabledStyles = css`
  cursor: default;
  color: ${gray2};
  pointer-events: none;
`;

const SubmissionFeedbackView = () => {
  const dispatch = useAppDispatch();
  const { injectServices } = useContext(AngularContext);
  const [$state, PageLevelManager, CurrentUserManager] = injectServices(['$state', 'PageLevelManager', 'CurrentUserManager']);
  const rolePlayId = Number($state?.params?.rolePlayId);

  const { submissionId: submissionIdParam } = useParams<{ submissionId: string }>();
  const submissionId = useMemo(() => {
    const parsed = Number(submissionIdParam);
    return Number.isFinite(parsed) ? parsed : null;
  }, [submissionIdParam]);

  const aiRolePlay = useAiRolePlay(rolePlayId);
  const submission = useSelector((state: RootState) => (
    submissionId ? getAiRolePlaySubmission(state, submissionId) : null
  ));

  const courseCatalogId = aiRolePlay?.courseCatalogId;
  const interactionMode = aiRolePlay?.interactionMode;
  const isHardDeadlineExpired = !!(aiRolePlay?.hardDeadline && aiRolePlay?.expired);

  const { isAdminOrMentor } = useRolePlayPermissions();
  const [isSubmissionFetched, setIsSubmissionFetched] = useState(false);

  // Redirect learners who try to view another user's submission.
  // Gated on isSubmissionFetched so we don't check against a skeleton entry that
  // was created by setAiRolePlaySubmissionCompleted without a user field, which
  // would cause a false redirect for the submitter themselves.
  useEffect(() => {
    if (!isSubmissionFetched || !submission || isAdminOrMentor) return;
    if (submission.user?.id !== CurrentUserManager?.user?.id) {
      $state.go('dashboard');
    }
  }, [isSubmissionFetched, submission, isAdminOrMentor, CurrentUserManager, $state]);

  const isTabletOrSmaller = useMediaQuery({ query: `(max-width: ${screenSmMax}px)` });

  // Messages and opening narrative are kept in local state, populated from the
  // conversation API via wrapThunkAction — same pattern as the old ConversationView.
  const [openingNarrative, setOpeningNarrative] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isMessagesLoading, setIsMessagesLoading] = useState(true);

  // Ensure the submission record (with feedback) is in Redux.
  // Redirect to dashboard if the fetch is rejected (e.g. 403 for another user's submission).
  useEffect(() => {
    if (!submissionId || !courseCatalogId) return undefined;
    setIsSubmissionFetched(false);
    let cancelled = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    wrapThunkAction(dispatch(fetchSubmission({ submissionId, catalogId: courseCatalogId })))
      .then(() => {
        if (!cancelled) setIsSubmissionFetched(true);
      })
      .catch((action: any) => {
        if (cancelled) return;
        if (action?.payload?.response?.status === 403) {
          dispatch(addAlertMessage({
            type: AlertMessageType.ERROR,
            header: t.COURSE_HOME.HEADER.COURSE_ACCESS_DENIED(),
          }));
          $state.go('dashboard');
        } else {
          dispatch(addAlertMessage({
            type: AlertMessageType.ERROR,
            header: t.FORM.SOMETHING_WRONG(),
            message: t.FORM.ERROR_TRY_AGAIN(),
          }));
        }
      });
    return () => { cancelled = true; };
  }, [submissionId, courseCatalogId, dispatch, $state]);

  useEffect(() => {
    if (!submissionId || !courseCatalogId) return undefined;
    let cancelled = false;
    setIsMessagesLoading(true);
    wrapThunkAction(dispatch(getAiRolePlayConversation({ submissionId, catalogId: courseCatalogId })))
      .then((res) => {
        if (cancelled) return;
        const { chatMessages = [] } = (res?.payload as { chatMessages?: Message[] }) || {};
        setOpeningNarrative(chatMessages[0]?.content || '');
        setMessages(chatMessages.slice(1).map((m) => ({
          ...m,
          id: m.id ?? uuid(),
          status: MessageStatus.SENT,
        })));
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setIsMessagesLoading(false);
      });
    return () => { cancelled = true; };
  }, [submissionId, courseCatalogId, dispatch]);

  const handlePracticeAgain = useCallback(() => {
    $state.go('role-play-submission-modal', { rolePlayId, section: 'new' }, { inherit: false });
  }, [$state, rolePlayId]);

  const handleExitChat = useCallback(() => {
    const lastParent = PageLevelManager?.lastParent?.();
    if (lastParent?.state?.name) {
      $state.go(lastParent.state.name, lastParent.params);
    } else {
      $state.go('dashboard');
    }
  }, [PageLevelManager, $state]);

  // Gallery navigation (admin-only, only when navigated from the gallery).
  const isAdmin = !!CurrentUserManager?.isAdmin?.();
  const fromGallery = !!$state?.params?.fromGallery;
  const showGalleryNav = isAdmin && fromGallery;

  const gallerySubmissionIds = useSelector((state: RootState) => getRolePlayGallerySubmissionIds(state, rolePlayId));
  const galleryState = useSelector((state: RootState) => getRolePlayGallerySubmissions(state, rolePlayId));
  const hasMore = !!galleryState?.hasMore;

  const currentIndex = showGalleryNav ? gallerySubmissionIds.indexOf(submissionId) : -1;
  const prevId = currentIndex > 0 ? gallerySubmissionIds[currentIndex - 1] : null;
  const isLastLoaded = currentIndex === gallerySubmissionIds.length - 1;
  const nextId = (!isLastLoaded && currentIndex >= 0) ? gallerySubmissionIds[currentIndex + 1] : null;

  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const prevGalleryLengthRef = useRef(gallerySubmissionIds.length);

  // When a page fetch completes and new IDs arrive, navigate to the first new one.
  useEffect(() => {
    if (!isFetchingNextPage) return;
    if (gallerySubmissionIds.length > prevGalleryLengthRef.current) {
      const firstNewId = gallerySubmissionIds[prevGalleryLengthRef.current];
      setIsFetchingNextPage(false);
      $state.go('role-play-submission-modal', { rolePlayId, section: firstNewId, fromGallery: true });
    }
  }, [gallerySubmissionIds, isFetchingNextPage, $state, rolePlayId]);

  const handleNavigateNext = useCallback(() => {
    if (nextId) {
      $state.go('role-play-submission-modal', { rolePlayId, section: nextId, fromGallery: true });
    } else if (hasMore && !isFetchingNextPage && courseCatalogId) {
      prevGalleryLengthRef.current = gallerySubmissionIds.length;
      setIsFetchingNextPage(true);
      const nextPage = Math.ceil(gallerySubmissionIds.length / PAGE_SIZE) + 1;
      wrapThunkAction(dispatch(getAiRolePlaySubmissionsList({
        catalogId: courseCatalogId,
        aiRolePlayId: rolePlayId,
        page: nextPage,
        pageSize: PAGE_SIZE,
        filterBy: 'all',
      }))).catch(() => setIsFetchingNextPage(false));
    }
  }, [nextId, hasMore, isFetchingNextPage, courseCatalogId, gallerySubmissionIds.length, $state, rolePlayId, dispatch]);

  const handleNavigatePrev = useCallback(() => {
    if (prevId) {
      $state.go('role-play-submission-modal', { rolePlayId, section: prevId, fromGallery: true });
    }
  }, [prevId, $state, rolePlayId]);

  const sessionData: SessionData = useMemo(() => ({
    aiApiEndpoint: null,
    jwtToken: null,
    submissionId,
    expiresAt: null,
    languageLocale: null,
  }), [submissionId]);

  const abortControllerRef = useRef<AbortController | null>(null);
  const conversationRef = useRef<HTMLDivElement>(null);

  const isBodyPartitioned = !isTabletOrSmaller;

  useEffect(() => {
    if (!isMessagesLoading && submission) {
      if (isBodyPartitioned) {
        conversationRef.current?.scrollTo({
          top: conversationRef.current.scrollHeight,
          behavior: 'smooth',
        });
      } else {
        conversationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    }
  }, [isMessagesLoading, submission, isBodyPartitioned]);
  const [showScenarioDetails, setShowScenarioDetails] = useState(true);

  if (!submissionId || !aiRolePlay) {
    return null;
  }

  const bodyStyles = css`
    display: flex;
    flex-direction: ${isBodyPartitioned ? 'row' : 'column'};
    min-height: 0;
    overflow-y: ${isBodyPartitioned ? 'hidden' : 'auto'};
    flex: 1;
  `;

  const conversationWrapperStyles = css`
    position: relative;
    flex: 1;
    display: flex;
    flex-direction: column;
    ${isBodyPartitioned && 'min-height: 0;'}
  `;

  const conversationStyles = css`
    flex: 1;
    overflow-y: ${isBodyPartitioned ? 'auto' : 'unset'};
    padding: ${standardSpacing}px;

    .scenario-toggle-button {
      margin-top: ${largeSpacing}px;
    }
    ${interactionMode === RolePlayInteractionMode.VOICE
        && submission?.closingReason === ClosingReason.TERMINATED
        && css`
          background-image: url(${prodPathReplace('images/voice-role-play-background.png')});
          background-size: cover;
          background-position: center;
      `}
  `;

  const feedbackStyles = css`
    flex: 1;
    overflow-y: ${isBodyPartitioned ? 'auto' : 'unset'};
    border-left: ${isBodyPartitioned ? `1px solid ${gray5}` : 'none'};
  `;

  const wrapperStyles = css`
    height: 100%;
    display: flex;
    flex-direction: column;
  `;

  const nextDisabled = !nextId && !hasMore;

  return (
    <AiConversationContext.Provider
      value={{
        interactionMode,
        aiRolePlayStatus: AIRolePlayStatus.COMPLETED,
        setAiRolePlayStatus: () => {},
        showScenarioDetails,
        setShowScenarioDetails,
        handleExitChat,
        aiRolePlayId: aiRolePlay.id,
        messages,
        setMessages: () => {},
        sessionData,
        setSessionData: () => {},
        isTimeout: false,
        setIsTimeout: () => {},
        isAwaitingAiResponse: false,
        abortControllerRef,
        isHardDeadlineExpired,
        isRolePlayStarting: false,
        setIsRolePlayStarting: () => {},
        openingNarrative,
        handleChunk: () => {},
        handleError: () => {},
        handleStartConversation: handlePracticeAgain,
        handleStartVoiceConversation: handlePracticeAgain,
        audioRecordingStatus: AudioRecordingStatus.NOT_STARTED,
        setAudioRecordingStatus: () => {},
      }}
    >
      <RolePlaySubmissionModalStyles />
      {showGalleryNav && (
        <>
          <button
            type='button'
            css={[leftArrowStyles, !prevId && galleryArrowDisabledStyles]}
            disabled={!prevId}
            aria-label={t.SHARED.PREVIOUS()}
            onClick={prevId ? handleNavigatePrev : undefined}
            data-qa={config.pendo.activities.aiRolePlay.galleryModal.previous}
          >
            <NvIcon icon='arrow-left' size='small' />
          </button>
          <button
            type='button'
            css={[rightArrowStyles, nextDisabled && galleryArrowDisabledStyles]}
            disabled={nextDisabled}
            aria-label={t.SHARED.NEXT()}
            onClick={!nextDisabled ? handleNavigateNext : undefined}
            data-qa={config.pendo.activities.aiRolePlay.galleryModal.next}
          >
            {isFetchingNextPage
              ? <span className='spinner-border spinner-border-sm' />
              : <NvIcon icon='arrow-right' size='small' />}
          </button>
        </>
      )}
      <div css={wrapperStyles}>
        <SubmissionFeedbackHeader
          submission={submission}
          rolePlayTitle={aiRolePlay.title}
          showUser={showGalleryNav}
          rolePlayId={rolePlayId}
          catalogId={courseCatalogId}
        />
        <div css={bodyStyles} className='border-top'>
          <div css={conversationWrapperStyles}>
            <LoadingWrapper isLoaded={!isMessagesLoading} className='flex-column flex-grow-1'>
              <div ref={conversationRef} css={conversationStyles}>
                {submission?.closingReason === ClosingReason.TERMINATED
                  && interactionMode === RolePlayInteractionMode.VOICE
                  && (
                    <VoiceCardsContainer />
                  )}
                <ScenarioSection />
                {interactionMode === RolePlayInteractionMode.VOICE
                  && submission?.closingReason === ClosingReason.TERMINATED
                  && (
                    <VoiceTerminatedStatus />
                  )}
                {interactionMode === RolePlayInteractionMode.VOICE
                  && submission?.closingReason === ClosingReason.COMPLETED
                  && (
                  <div className='mt-4'>
                    {submission.mergedPlaybackUrl && (
                    <audio
                      controls
                      src={submission.mergedPlaybackUrl}
                      className='w-100'
                    />
                    )}
                    <ChatTranscriptView messages={messages} />
                  </div>
                  )}
                {interactionMode === RolePlayInteractionMode.TEXT && (
                  <ConversationReplay
                    messages={messages}
                    openingNarrative={openingNarrative}
                  />
                )}
                {interactionMode === RolePlayInteractionMode.TEXT && <CompletionSection />}
              </div>
              <ScrollHandlerButton containerRef={conversationRef} />
            </LoadingWrapper>
          </div>
          {submission?.feedback && (
            <div css={feedbackStyles}>
              <AiFeedback submissionId={submissionId} />
            </div>
          )}
        </div>
      </div>
    </AiConversationContext.Provider>
  );
};

export default SubmissionFeedbackView;
